/**
 * "Viewing as <agent> — read only — Exit" toolbar (D2-SEC-011, #3944).
 *
 * Shown while a super-user/manager is viewing the CRM exactly as one agent
 * sees it. The server tells us (the `view_as` block on `/auth/me/`), so the
 * banner can never claim a view-as the server is not actually serving. Same
 * in-flow, top-mounted toolbar treatment as the persona PreviewBanner (#1492):
 * it pushes the page down, never overlaps it, and the Exit target is >=44px.
 */
import { useEffect, useState } from "react";
import { Eye, Loader2, X } from "lucide-react";
import { useAuth } from "../../contexts/auth";
import { stopViewAs } from "../../api/viewAs";
import { leaveViewAs, VIEW_AS_REFUSED_EVENT } from "../../lib/viewAs";
import { useToast } from "../ui/useToast";

export function ViewAsBanner() {
  const { user } = useAuth();
  const toast = useToast();
  const [exiting, setExiting] = useState(false);
  const viewAs = user?.view_as;
  const targetName = viewAs?.target.name;

  // #3962: a write the server refused while viewing-as becomes ONE
  // non-destructive toast here, so no page has to replace its content with an
  // error box. Write affordances are hidden (`useIsViewingAs`), so this is the
  // backstop for anything that still reaches the server.
  useEffect(() => {
    if (!targetName) return;
    function onRefused() {
      toast.info(
        `You are viewing as ${targetName}, read only. Exit view-as to make changes.`,
        { title: "Nothing was changed" },
      );
    }
    window.addEventListener(VIEW_AS_REFUSED_EVENT, onRefused);
    return () => window.removeEventListener(VIEW_AS_REFUSED_EVENT, onRefused);
  }, [targetName, toast]);

  if (!viewAs) return null;

  const name = viewAs.target.name;

  async function handleExit() {
    setExiting(true);
    await stopViewAs();
    // Full reload so every cached page state is re-fetched as the viewer.
    leaveViewAs("/");
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="view-as-banner"
      title="You are seeing the CRM exactly as this agent sees it. Nothing can be changed."
      className="flex w-full shrink-0 items-center gap-2 border-b border-white/10 bg-navy px-4 py-2 text-white sm:px-6"
    >
      <Eye className="h-4 w-4 shrink-0 text-brand-accent" aria-hidden="true" />
      <span className="min-w-0 flex-1 text-xs font-medium sm:text-sm">
        {/* #3962: both lines WRAP (never truncate) so the full sentence is
            readable at 375px wide. */}
        <span className="block break-words">
          Viewing as <span className="font-semibold">{name}</span> — read only
        </span>
        <span className="block break-words text-[0.6875rem] font-normal text-white/75 sm:text-xs">
          You see what {name} sees. Changes, exports and downloads are switched off.
        </span>
      </span>
      <button
        type="button"
        onClick={handleExit}
        disabled={exiting}
        aria-label={`Exit view as ${name} and return to your own account`}
        className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-md bg-brand-accent px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-accent-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy disabled:opacity-70 sm:text-sm"
      >
        {exiting ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <X className="h-4 w-4" aria-hidden="true" />
        )}
        <span>Exit</span>
      </button>
    </div>
  );
}
